import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, inr, getCart, setCart, clearCart, customerSession } from "@/lib/api";
import { CustomerHeader, EmptyState } from "@/components/tabdine/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { UtensilsCrossed, ShoppingBag } from "lucide-react";

const phoneValid = (p) => /^[6-9]\d{9}$/.test((p || "").trim());

const LoginSheet = ({ open, onClose, onDone, slugForReturn }) => {
  const [mode, setMode] = useState("login");
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [errors, setErrors] = useState({});

  const validate = () => {
    const e = {};
    if (!phoneValid(phone)) e.phone = "Enter a valid 10-digit mobile";
    if (!password || password.length < 6) e.password = "Min 6 characters";
    if (mode === "signup") {
      if (!name.trim()) e.name = "Name required";
      if (password !== confirm) e.confirm = "Passwords don't match";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async () => {
    if (!validate()) return;
    try {
      const url = mode === "login" ? "/customer/login" : "/customer/register";
      const body = mode === "login" ? { phone, password } : { phone, name, password };
      const res = await api.post(url, body);
      customerSession.set(res.data.token, res.data.customer);
      toast.success(mode === "login" ? "Welcome back!" : "Account created!");
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Login failed");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="login-sheet">
        <DialogHeader>
          <DialogTitle className="font-heading">
            {mode === "login" ? "Login to continue" : "Create account to continue"}
          </DialogTitle>
          <DialogDescription>Your cart is saved. Continue when you're ready.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs text-brand-dark/60">Phone</label>
            <Input value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="sheet-phone" inputMode="numeric" />
            {errors.phone && <div className="text-xs text-red-500 mt-1">{errors.phone}</div>}
          </div>
          {mode === "signup" && (
            <div>
              <label className="text-xs text-brand-dark/60">Full name</label>
              <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="sheet-name" />
              {errors.name && <div className="text-xs text-red-500 mt-1">{errors.name}</div>}
            </div>
          )}
          <div>
            <label className="text-xs text-brand-dark/60">Password</label>
            <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="sheet-password" />
            {errors.password && <div className="text-xs text-red-500 mt-1">{errors.password}</div>}
          </div>
          {mode === "signup" && (
            <div>
              <label className="text-xs text-brand-dark/60">Confirm password</label>
              <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="sheet-confirm" />
              {errors.confirm && <div className="text-xs text-red-500 mt-1">{errors.confirm}</div>}
            </div>
          )}
          <Button onClick={submit} className="w-full bg-brand-orange text-white" data-testid="sheet-submit">
            {mode === "login" ? "Sign in" : "Create account & continue"}
          </Button>
          <button onClick={() => { setMode(mode === "login" ? "signup" : "login"); setErrors({}); }} className="text-xs text-brand-dark/60 w-full text-center" data-testid="sheet-switch">
            {mode === "login" ? "New here? Sign up →" : "Have an account? Sign in →"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default function CartPage() {
  const [params] = useSearchParams();
  const slug = params.get("r");
  const tableId = params.get("table");
  const nav = useNavigate();

  const [items, setItemsState] = useState([]);
  const [restaurant, setRestaurant] = useState(null);
  const [note, setNote] = useState("");
  const [offerCode, setOfferCode] = useState("");
  const [splitCount, setSplitCount] = useState(1);
  const [showLogin, setShowLogin] = useState(false);
  const [pendingMethod, setPendingMethod] = useState(null);
  const [busy, setBusy] = useState(false);
  const [charges, setCharges] = useState([]);
  const [orderType, setOrderType] = useState(tableId ? "dine_in" : "");
  const [orderTypeError, setOrderTypeError] = useState(false);
  const [offers, setOffers] = useState([]);
  const [appliedOffer, setAppliedOffer] = useState(null);
  const [offerError, setOfferError] = useState("");

  useEffect(() => {
    if (!slug) return;
    setItemsState(getCart(slug));
    api.get(`/restaurants/${slug}`).then((res) => setRestaurant(res.data));
    api.get(`/restaurants/${slug}/charges`).then((res) => setCharges(res.data));
    api.get(`/restaurants/${slug}/offers`).then((res) => setOffers(res.data));
  }, [slug]);

  const setQty = (id, delta) => {
    let next = items.map((c) => (c.item_id === id ? { ...c, quantity: c.quantity + delta } : c));
    next = next.filter((c) => c.quantity > 0);
    setItemsState(next);
    setCart(slug, next);
  };

  const subtotal = items.reduce((s, i) => s + i.item_price * i.quantity, 0);
  const tax = subtotal * 0.05;
  // Preview charges (backend re-computes authoritatively at order creation)
  const chargeLines = charges.map((c) => ({
    ...c,
    amount: c.charge_type === "percentage" ? +(subtotal * c.value / 100).toFixed(2) : +c.value.toFixed(2),
  }));
  const chargesTotal = chargeLines.reduce((s, c) => s + c.amount, 0);

  // Mirrors the discount logic in orders-api exactly, for a live preview.
  let discountAmount = 0;
  if (appliedOffer) {
    if (appliedOffer.offer_type === "percent") {
      discountAmount = subtotal * (appliedOffer.discount_value / 100);
      if (appliedOffer.max_discount_cap) discountAmount = Math.min(discountAmount, appliedOffer.max_discount_cap);
    } else if (appliedOffer.offer_type === "flat") {
      discountAmount = appliedOffer.discount_value;
    }
  }

  const total = subtotal + tax + chargesTotal - discountAmount;
  const perPerson = total / Math.max(1, splitCount);

  const applyOffer = () => {
    setOfferError("");
    setAppliedOffer(null);
    const code = offerCode.trim().toUpperCase();
    if (!code) return;
    const match = offers.find((o) => o.code?.toUpperCase() === code);
    if (!match) {
      setOfferError("Invalid or expired code");
      return;
    }
    if (subtotal < (match.min_order_value || 0)) {
      setOfferError(`Add ${inr(match.min_order_value - subtotal)} more to use this code`);
      return;
    }
    setAppliedOffer(match);
  };

  const removeOffer = () => {
    setAppliedOffer(null);
    setOfferError("");
    setOfferCode("");
  };

  const placeOrder = async (method) => {
    if (!orderType) {
      setOrderTypeError(true);
      toast.error("Please select Dine-In or Takeaway");
      return;
    }
    if (!customerSession.get()) {
      setPendingMethod(method);
      setShowLogin(true);
      return;
    }
    setBusy(true);
    try {
      const res = await api.post("/orders", {
        restaurant_slug: slug,
        table_id: tableId,
        items: items.map((i) => ({ item_id: i.item_id, quantity: i.quantity })),
        special_instructions: note,
        payment_method: method,
        offer_code: appliedOffer?.code || null,
        split_count: splitCount,
        order_type: orderType,
      });
      clearCart(slug);
      toast.success("Order placed!");
      nav(`/order-confirm?order=${res.data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not place order");
    }
    setBusy(false);
  };

  const onLoginDone = () => {
    setShowLogin(false);
    if (pendingMethod) {
      const m = pendingMethod;
      setPendingMethod(null);
      setTimeout(() => placeOrder(m), 100);
    }
  };

  if (!slug || !restaurant) return <div className="min-h-screen grid place-items-center">Loading…</div>;

  return (
    <div className="min-h-screen bg-brand-light">
      <CustomerHeader />
      <div className="max-w-2xl mx-auto px-4 py-6">
        <div className="mb-3">
          <div className="text-xs text-brand-dark/60">Your order at</div>
          <div className="font-heading text-xl font-semibold text-brand-dark">{restaurant.name}</div>
          {tableId && <div className="text-xs text-brand-dark/60 mt-0.5">Table service</div>}
        </div>

        {items.length === 0 ? (
          <EmptyState title="Cart is empty" subtitle="Head back to the menu to add dishes.">
            <Button onClick={() => nav(`/?r=${slug}`)} className="bg-brand-orange text-white">Browse menu</Button>
          </EmptyState>
        ) : (
          <>
            <div className="bg-white rounded-2xl border border-brand-dark/5 p-4 space-y-3" data-testid="cart-items">
              {items.map((it) => (
                <div key={it.item_id} className="flex items-center gap-3" data-testid={`cart-line-${it.item_id}`}>
                  <img src={it.image_url} alt="" className="w-12 h-12 rounded-lg object-cover bg-brand-orange/5" />
                  <div className="flex-1">
                    <div className="font-medium text-sm">{it.item_name}</div>
                    <div className="text-xs text-brand-dark/60">{inr(it.item_price)}</div>
                  </div>
                  <div className="flex items-center gap-2 bg-brand-orange/10 rounded-full">
                    <button onClick={() => setQty(it.item_id, -1)} className="w-8 h-8 text-brand-orange font-bold" data-testid={`cart-dec-${it.item_id}`}>−</button>
                    <span className="text-sm w-4 text-center">{it.quantity}</span>
                    <button onClick={() => setQty(it.item_id, 1)} className="w-8 h-8 text-brand-orange font-bold" data-testid={`cart-inc-${it.item_id}`}>+</button>
                  </div>
                  <div className="w-16 text-right font-heading font-semibold text-sm">{inr(it.item_price * it.quantity)}</div>
                </div>
              ))}
            </div>

            {/* Order Type */}
            <div className={`mt-3 bg-white rounded-2xl border p-4 ${orderTypeError && !orderType ? "border-red-400" : "border-brand-dark/5"}`}>
              <div className="text-xs uppercase tracking-widest text-brand-dark/50 mb-2">
                Order type <span className="text-red-500">*</span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <button
                  data-testid="order-type-dine_in"
                  onClick={() => { setOrderType("dine_in"); setOrderTypeError(false); }}
                  className={`p-3 rounded-xl border-2 flex flex-col items-center gap-1 transition-colors ${orderType === "dine_in" ? "border-brand-orange bg-brand-orange/5 text-brand-orange" : "border-brand-dark/10 text-brand-dark/70 hover:border-brand-dark/30"}`}
                >
                  <UtensilsCrossed size={20} />
                  <span className="text-sm font-medium">Dine-In</span>
                </button>
                <button
                  data-testid="order-type-takeaway"
                  onClick={() => { setOrderType("takeaway"); setOrderTypeError(false); }}
                  className={`p-3 rounded-xl border-2 flex flex-col items-center gap-1 transition-colors ${orderType === "takeaway" ? "border-brand-orange bg-brand-orange/5 text-brand-orange" : "border-brand-dark/10 text-brand-dark/70 hover:border-brand-dark/30"}`}
                >
                  <ShoppingBag size={20} />
                  <span className="text-sm font-medium">Takeaway</span>
                </button>
              </div>
              {orderTypeError && !orderType && (
                <div className="text-xs text-red-500 mt-2" data-testid="order-type-error">Please select Dine-In or Takeaway to continue.</div>
              )}
            </div>

            <div className="mt-3 bg-white rounded-2xl border border-brand-dark/5 p-4 space-y-3">
              <div>
                <label className="text-xs text-brand-dark/60">Special instructions</label>
                <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Extra spicy, less oil…" data-testid="cart-note" />
              </div>
              <div className="flex gap-2 items-end">
                <div className="flex-1">
                  <label className="text-xs text-brand-dark/60">Discount code</label>
                  {appliedOffer ? (
                    <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-md px-3 py-2 text-sm text-green-700" data-testid="cart-offer-applied">
                      <span>"{appliedOffer.code}" applied</span>
                      <button onClick={removeOffer} className="text-xs underline" data-testid="cart-offer-remove">Remove</button>
                    </div>
                  ) : (
                    <Input
                      value={offerCode}
                      onChange={(e) => { setOfferCode(e.target.value.toUpperCase()); setOfferError(""); }}
                      onKeyDown={(e) => e.key === "Enter" && applyOffer()}
                      placeholder="TABFIRST"
                      data-testid="cart-offer"
                    />
                  )}
                </div>
                {!appliedOffer && (
                  <Button variant="outline" onClick={applyOffer} data-testid="cart-offer-apply">Apply</Button>
                )}
                <div className="w-24">
                  <label className="text-xs text-brand-dark/60">Split</label>
                  <Input type="number" min={1} value={splitCount} onChange={(e) => setSplitCount(Math.max(1, +e.target.value || 1))} data-testid="cart-split" />
                </div>
              </div>
              {offerError && <div className="text-xs text-red-500" data-testid="cart-offer-error">{offerError}</div>}
            </div>

            <div className="mt-3 bg-white rounded-2xl border border-brand-dark/5 p-4">
              <div className="flex justify-between text-sm py-0.5">
                <span className="text-brand-dark/70">Subtotal</span>
                <span>{inr(subtotal)}</span>
              </div>
              <div className="flex justify-between text-sm py-0.5">
                <span className="text-brand-dark/70">GST (5%)</span>
                <span>{inr(tax)}</span>
              </div>
              {appliedOffer && discountAmount > 0 && (
                <div className="flex justify-between text-sm py-0.5 text-green-700" data-testid="cart-discount-line">
                  <span>Discount ({appliedOffer.code})</span>
                  <span>−{inr(discountAmount)}</span>
                </div>
              )}
              {chargeLines.map((c) => (
                <div key={c.id} className="flex justify-between text-sm py-0.5" data-testid={`charge-${c.name}`}>
                  <span className="text-brand-dark/70">
                    {c.name} {c.charge_type === "percentage" && <span className="text-xs">({c.value}%)</span>}
                  </span>
                  <span>{inr(c.amount)}</span>
                </div>
              ))}
              <div className="border-t border-brand-dark/10 my-2" />
              <div className="flex justify-between font-heading font-semibold" data-testid="cart-total">
                <span>Total</span>
                <span>{inr(total)}</span>
              </div>
              {splitCount > 1 && (
                <div className="text-xs text-brand-dark/60 mt-1">Per person: {inr(perPerson)}</div>
              )}
            </div>

            <div className="mt-4 grid gap-2">
              <Button disabled className="w-full bg-brand-dark/10 text-brand-dark/50 cursor-not-allowed" data-testid="pay-now-btn">
                Pay Now (Coming Soon)
              </Button>
              <Button
                onClick={() => placeOrder("pay_after_dinner")}
                disabled={busy}
                className="w-full bg-brand-orange hover:bg-brand-orange-dark text-white"
                data-testid="pay-after-dinner-btn"
              >
                {busy ? "Placing..." : "Pay After Dinner — Place Order"}
              </Button>
            </div>
          </>
        )}
      </div>

      <LoginSheet
        open={showLogin}
        onClose={() => { setShowLogin(false); setPendingMethod(null); }}
        onDone={onLoginDone}
        slugForReturn={slug}
      />
    </div>
  );
}
